import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import Confetti from './Confetti';
import { trackStreakMilestone } from '../utils/analytics';
import StreakShieldUI from './StreakShieldUI';
import safeStorage from '../utils/safeStorage';

// Family reading streak card for the dashboard, with milestone badges and a
// confetti celebration when a milestone is first reached.
const MILESTONES = [
  { nights: 7,   emoji: '🔥', label: '7-Night Hero' },
  { nights: 30,  emoji: '🌟', label: '30-Night Legend' },
  { nights: 100, emoji: '👑', label: '100-Night Royalty' },
];

const StreakCard = () => {
  const [streak, setStreak] = useState(null);
  const [freezeInfo, setFreezeInfo] = useState(null);
  const [celebrating, setCelebrating] = useState(null); // milestone being celebrated

  const loadStreak = useCallback(() => {
    API.get('/users/streak')
      .then(res => setStreak(res.data.streak))
      .catch(() => {});
    API.get('/streaks/status')
      .then(res => setFreezeInfo(res.data.streak))
      .catch(() => {});
  }, []);

  useEffect(() => { loadStreak(); }, [loadStreak]);

  useEffect(() => {
    if (!streak || streak.currentStreak < MILESTONES[0].nights) return undefined;
    const hit = [...MILESTONES].reverse().find(m => streak.currentStreak >= m.nights);
    if (!hit) return undefined;
    // Celebrate each milestone once per streak run (keyed by the run's start day)
    const runStart = new Date(Date.now() - (streak.currentStreak - 1) * 86400000).toISOString().slice(0, 10);
    const key = `streakCeleb_${hit.nights}_${runStart}`;
    if (safeStorage.getItem(key)) return undefined;
    safeStorage.setItem(key, '1');
    setCelebrating(hit);
    trackStreakMilestone(hit.nights);
    const t = setTimeout(() => setCelebrating(null), 5000);
    return () => clearTimeout(t);
  }, [streak]);

  if (!streak) return null;

  const { currentStreak, longestStreak, activeToday, atRisk } = streak;
  const badges = MILESTONES.map(m => ({ ...m, earned: longestStreak >= m.nights }));
  const anyBadge = badges.some(b => b.earned);

  return (
    <>
      {celebrating && <Confetti />}
      <div className="rounded-4 p-4 mb-4" style={{background:'var(--card-bg)', border: atRisk ? '2px solid #f9c74f' : '1px solid var(--border-purple)'}}>
        {celebrating && (
          <div className="text-center mb-3 p-3 rounded-3" style={{background:'var(--yellow-section)', border:'1px solid var(--border-yellow)'}}>
            <span style={{fontSize:'22px'}}>{celebrating.emoji}</span>{' '}
            <strong style={{color:'var(--text-main)', fontSize:'16px'}}>
              {celebrating.nights}-night milestone unlocked — you're a {celebrating.label}!
            </strong>
          </div>
        )}

        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{width:'56px', height:'56px', background: currentStreak > 0 ? 'var(--orange-section)' : 'var(--purple-section)', fontSize:'26px'}}>
            {currentStreak > 0 ? '🔥' : '🌙'}
          </div>

          <div className="flex-grow-1">
            {currentStreak > 0 ? (
              <>
                <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'17px', fontFamily:"'Fredoka One',cursive"}}>
                  {currentStreak}-night streak!
                </div>
                <div style={{color:'var(--text-muted2)', fontSize:'13px'}}>
                  {activeToday
                    ? <>Tonight's story is done — see you tomorrow ✨</>
                    : <>Keep it going tonight — one story is all it takes</>}
                  {longestStreak > currentStreak && <> · Best: {longestStreak} nights</>}
                </div>
              </>
            ) : (
              <>
                <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'17px', fontFamily:"'Fredoka One',cursive"}}>
                  Start a streak tonight
                </div>
                <div style={{color:'var(--text-muted2)', fontSize:'13px'}}>
                  Read one story each night and watch the streak grow
                  {longestStreak > 0 && <> · Best: {longestStreak} nights</>}
                </div>
              </>
            )}
          </div>

          {atRisk && (
            <Link to="/dashboard/generate" className="btn btn-warning rounded-pill fw-bold px-4 flex-shrink-0">
              Keep the streak 🔥
            </Link>
          )}
        </div>

        {anyBadge && (
          <div className="d-flex flex-wrap gap-2 mt-3 pt-3" style={{borderTop:'1px solid var(--row-sep)'}}>
            {badges.map(b => (
              <span key={b.nights} className="badge rounded-pill d-flex align-items-center gap-1"
                title={b.earned ? `Earned: ${b.label}` : `Reach a ${b.nights}-night streak to unlock`}
                style={b.earned
                  ? {background:'var(--badge-purple-bg)', color:'var(--text-main)', fontSize:'12px', padding:'7px 12px'}
                  : {background:'var(--item-bg)', color:'var(--text-soft)', fontSize:'12px', padding:'7px 12px', opacity:0.65}}>
                {b.emoji} {b.label}{!b.earned && ' 🔒'}
              </span>
            ))}
          </div>
        )}

        {/* Streak freeze / repair */}
        {freezeInfo && (
          <StreakShieldUI
            freezeTokens={freezeInfo.freezeTokens ?? 0}
            canRepair={freezeInfo.canRepair ?? false}
            currentStreak={currentStreak}
            onUpdate={loadStreak}
          />
        )}
      </div>
    </>
  );
};

export default StreakCard;
